import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { ProviderSearchDialog } from "@/components/ProviderSearchDialog";
import { Inbox, Mail, Phone, Check, MapPin, StickyNote, Search, MessageSquare } from "lucide-react";

const LeadNote = ({ lead }) => {
  const [note, setNote] = useState(lead.note || "");
  const [saving, setSaving] = useState(false);
  const dirty = note !== (lead.note || "");
  const save = async () => {
    setSaving(true);
    try { await api.put(`/leads/${lead.id}/note`, { note }); lead.note = note; toast.success("Nota guardada"); }
    catch { toast.error("No se pudo guardar la nota"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3 rounded-sm bg-slate-50 p-2.5" data-testid={`lead-note-${lead.id}`}>
      <label className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500"><StickyNote className="h-3.5 w-3.5" /> Nota privada</label>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Acuerdos con el cliente o el proveedor…"
        className="mt-1.5 w-full resize-y rounded-sm border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-primary" data-testid={`lead-note-input-${lead.id}`} />
      {dirty && <button onClick={save} disabled={saving} className="mt-1.5 rounded-sm bg-primary px-3 py-1 text-xs font-bold text-white hover:bg-orange-600" data-testid={`lead-note-save-${lead.id}`}>{saving ? "Guardando…" : "Guardar nota"}</button>}
    </div>
  );
};

export default function Leads() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [isMaster, setIsMaster] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [hired, setHired] = useState({});
  const [search, setSearch] = useState("");

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  const loadHired = () => api.get("/admin/hired-providers").then((r) => setHired(r.data.hired || {})).catch(() => {});
  useEffect(() => {
    load();
    api.get("/businesses/mine").then((r) => { const m = !!r.data?.is_master; setIsMaster(m); if (m) loadHired(); }).catch(() => {});
  }, []);

  const markDone = async (id) => {
    try { await api.put(`/leads/${id}/status`, null, { params: { status: "done" } }); toast.success("Marcada como atendida"); load(); }
    catch { toast.error("Error"); }
  };

  const startChat = async (lead) => {
    try {
      const r = await api.post(`/leads/${lead.id}/start-chat`);
      toast.success(r.data.emailed ? "Chat abierto. Se envió el enlace al cliente por email." : (r.data.existing ? "Chat ya existente, abriendo…" : "Chat abierto."));
      navigate(`/dashboard/messages?open=${r.data.session_id}`);
    } catch { toast.error("No se pudo abrir el chat"); }
  };

  const q = search.trim().toLowerCase();
  const filtered = leads.filter((l) => (filter === "all" ? true : filter === "done" ? l.status === "done" : l.status !== "done"))
    .filter((l) => !q || [l.name, l.city, l.interest, l.message, l.email].some((v) => (v || "").toLowerCase().includes(q)));
  const pendingCount = leads.filter((l) => l.status !== "done").length;

  return (
    <div data-testid="leads-page">
      <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-secondary"><Inbox className="h-6 w-6 text-primary" /> {t("dash_leads")}</h1>
      <p className="mt-1 text-sm text-slate-500">Solicitudes de clientes recibidas desde tu página pública.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="leads-filter">
        {[["pending", `Sin atender${pendingCount ? ` (${pendingCount})` : ""}`], ["done", "Atendidas"], ["all", "Todas"]].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)} data-testid={`leads-filter-${val}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-bold transition-colors ${filter === val ? "bg-primary text-white" : "border border-slate-200 text-slate-500 hover:border-slate-300"}`}>{label}</button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o ciudad…"
            className="w-full rounded-full border border-slate-200 py-1.5 pl-8 pr-3 text-sm outline-none focus:border-primary" data-testid="leads-search-input" />
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {filtered.map((l) => (
          <div key={l.id} className="rounded-sm border border-slate-200 bg-white p-4" data-testid={`lead-${l.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-head font-bold text-secondary">{l.name} {l.referred_by_urbis && <span className="ml-1 inline-flex items-center gap-1 rounded-sm bg-primary/10 px-2 py-0.5 text-xs font-bold uppercase text-primary" data-testid={`lead-referred-${l.id}`}>Referido por URBIS</span>} {l.status === "done" && <span className="ml-2 rounded-sm bg-accent/10 px-2 py-0.5 text-xs font-bold uppercase text-accent">Atendida</span>}{hired[l.id] && <span className="ml-2 inline-flex items-center gap-1 rounded-sm bg-accent px-2 py-0.5 text-xs font-bold text-white" data-testid={`lead-hired-${l.id}`}><Check className="h-3 w-3" />Contratado: {hired[l.id]}</span>}</p>
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
                  {l.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</span>}
                  {l.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</span>}
                  {l.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{l.city}</span>}
                  {l.interest && <span className="rounded-sm bg-primary/10 px-2 py-0.5 font-bold text-primary">{l.interest}</span>}
                </div>
                <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{l.message}</p>
                <p className="mt-1 text-xs text-slate-400">{(l.created_at || "").slice(0, 16).replace("T", " ")}</p>
                {isMaster && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <ProviderSearchDialog defaultCity={l.city || ""} defaultCategory={l.interest || ""} jobMessage={l.message || ""} contextId={l.id} clientName={l.name || ""} clientPhone={l.phone || ""} clientEmail={l.email || ""} onStatusChange={loadHired} testId={`lead-providers-${l.id}`} />
                    <button onClick={() => startChat(l)} data-testid={`lead-chat-${l.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-secondary/30 px-3 py-1.5 text-sm font-bold text-secondary hover:bg-secondary hover:text-white">
                      <MessageSquare className="h-3.5 w-3.5" /> Abrir chat
                    </button>
                  </div>
                )}
              </div>
              {l.status !== "done" && <button onClick={() => markDone(l.id)} className="rounded-sm p-2 text-accent hover:bg-accent/10" title="Marcar atendida" data-testid={`lead-done-${l.id}`}><Check className="h-4 w-4" /></button>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="rounded-sm border border-dashed border-slate-200 p-10 text-center text-slate-400">{q ? "No hay solicitudes que coincidan con tu búsqueda." : filter === "pending" ? "No tienes solicitudes sin atender." : filter === "done" ? "Aún no hay solicitudes atendidas." : "Aún no hay solicitudes."}</p>}
      </div>
    </div>
  );
}
